import { Spin } from "antd";
import { createContext, useCallback, useEffect, useRef, useState } from "react";
import axios from "../config/axios";
import { readStoredLocation, writeStoredLocation } from "../utils/storage";

export const AuthContext = createContext({
    user: {},
    setUser: () => {},
    isAuthenticated: false,
    setIsAuthenticated: () => {},
});

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    // start from what the browser remembers so the location popup never flashes
    // up while /users/authenticate is still in flight
    const [location, setLocation] = useState(readStoredLocation);

    // keeps state and local storage in step, without touching the account
    const applyLocation = useCallback((newLocation) => {
        setLocation(newLocation);
        writeStoredLocation(newLocation);
    }, []);

    // what the location popup calls: remember the location here, and - when we
    // have a session - on the account, so the next login already knows it
    const saveLocation = useCallback(
        async (newLocation) => {
            applyLocation(newLocation);

            if (!newLocation || !isAuthenticated) return;

            try {
                await axios.patch(
                    "/users/location",
                    { delivery_location: newLocation },
                    { withCredentials: true }
                );
                setUser((current) => (current ? { ...current, delivery_location: newLocation } : current));
            } catch (error) {
                // the popup has already done its job in this browser; a failed
                // sync only means the next login falls back to local storage
                console.error("Could not save the delivery location to the account", error);
            }
        },
        [applyLocation, isAuthenticated]
    );

    useEffect(() => {
        (async () => {
            try {
                const response = await axios.get("/users/authenticate", {
                    withCredentials: true,
                });
                setIsAuthenticated(true);
                let userData = response.data;
                userData.wishlist = !userData.wishlist ? [] : userData.wishlist;

                setUser(userData);

                // apply the account's location before the app paints, so a
                // signed-in customer never sees the "where do we deliver?" state
                if (userData.delivery_location) applyLocation(userData.delivery_location);
            } catch (error) {
                setIsAuthenticated(false);
                setUser({});
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reconcile account and browser once per signed-in user - on page load and
    // again right after a login. The account is the source of truth; when it has
    // no location yet, the one this browser already collected becomes it, which
    // is the "save it the first time" half of the popup.
    const reconciledUserId = useRef(null);

    useEffect(() => {
        if (!isAuthenticated || !user?.id) {
            reconciledUserId.current = null;
            return;
        }

        if (reconciledUserId.current === user.id) return;
        reconciledUserId.current = user.id;

        if (user.delivery_location) {
            applyLocation(user.delivery_location);
        } else {
            const storedLocation = readStoredLocation();
            if (storedLocation) saveLocation(storedLocation);
        }
    }, [isAuthenticated, user?.id, user?.delivery_location, applyLocation, saveLocation]);

    return (
        <>
            {loading ? (
                <div style={{ display: "grid", placeItems: "center", height: "100vh" }}>
                    <Spin size="large" />
                </div>
            ) : (
                <AuthContext.Provider
                    value={{
                        user,
                        setUser,
                        isAuthenticated,
                        setIsAuthenticated,
                        location,
                        setLocation: applyLocation,
                        saveLocation,
                    }}
                >
                    {children}
                </AuthContext.Provider>
            )}
        </>
    );
};
